import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAuth } from 'firebase/auth';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// auth
import { useAuthContext, useRecaptcha } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import useFetcher, { endpoints } from 'src/utils/axios';
import SalesforceLoginDialog from '../salesforce/salesforce-login-dialog';
// ----------------------------------------------------------------------

export default function FirebaseRegisterView() {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const inviteKey = urlParams.get('inviteKey');
  const returnTo = encodeURIComponent(urlParams.get('returnTo') || '');
  const params = encodeURIComponent(urlParams.get('params') || '');

  const auth = getAuth();
  const recaptcha = useRecaptcha(auth, 'recaptcha-container-for-login');

  const { register, loginWithGoogle, register_with_invite, checkUserSecondVerification } = useAuthContext();
  const { fetchData } = useFetcher();
  const [errorMsg, setErrorMsg] = useState('');
  const [buttonText, setButtonText] = useState('Create Account and Organization');

  const showSalesforceLoginDialog = useBoolean();

  const router = useRouter();

  const password = useBoolean();
  const RegisterSchema = Yup.object().shape({
    firstName: Yup.string().required('First name required'),
    lastName:  Yup.string().required('Last name required'),
    email:     Yup.string().required('Email is required').email('Email must be a valid email address'),
    password:  Yup.string()
      .required('Password is required')
      .test('password-policy', function (value) {
        const { path, createError } = this;
        if (!value) return true;
  
        const missing: string[] = [];
        if (value.length < 8)               missing.push('at least 8 characters');
        if (!/[A-Z]/.test(value))           missing.push('an uppercase letter');
        if (!/[a-z]/.test(value))           missing.push('a lowercase letter');
        if (!/[0-9]/.test(value))           missing.push('a number');
        if (!/[^A-Za-z0-9]/.test(value))     missing.push('a special character');
  
        if (missing.length === 0) return true;
  
        return createError({
          path,
          message: `Missing password requirements: [${missing.join(', ')}]`,
        });
      }),
  });  

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    setValue,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const getEmailFromInviteKey = () => {
    fetchData([endpoints.users.mail_from_invite_key, { params: { inviteKey } }]).then((res) => {
      setValue('email', res);
    });
  };
  useEffect(() => {
    if (inviteKey) {
      getEmailFromInviteKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteKey]);
  const onSubmit = handleSubmit(async (data) => {
    try {
      if (inviteKey) {
        await register_with_invite?.(
          data.email,
          data.password,
          data.firstName,
          data.lastName,
          inviteKey
        );
        const searchParams = new URLSearchParams({ email: data.email }).toString();
        const href = `${paths.auth.firebase.verify}?${searchParams}`;
        router.push(href);
      } else {
        const response = await register?.(
          data.email,
          data.password,
          data.firstName,
          data.lastName
        );
  
        if (response?.status === 'failed') {
          setErrorMsg(response.message);
        } else {
          const searchParams = new URLSearchParams({ email: data.email }).toString();
          const href = `${paths.auth.firebase.verify}?${searchParams}`;
          router.push(href);
        }
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(typeof error === 'string' ? error : error.message);
      reset();
      if (inviteKey) {
        getEmailFromInviteKey();
      }
    }
  });

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle?.(inviteKey);
      router.push(
        paths.auth.firebase.selectSecondVerification,
        `${inviteKey ? `inviteKey=${inviteKey}` : ''}${returnTo ? `&returnTo=${returnTo}` : ''}${
          params ? `&params=${params}` : ''
        }`
      );
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        if (!recaptcha) {
          setErrorMsg('Invalid Recaptcha!');
          return;
        }
        await checkUserSecondVerification?.(error, recaptcha, inviteKey, returnTo, params);
      } else {
        console.error(error);
        setErrorMsg(typeof error === 'string' ? error : error.message);
      }
    }
  };

  useEffect(() => {
    if (inviteKey) {
      setButtonText('Create account and join organization');
    }
  }, [inviteKey]);
  const renderHead = (
    <Stack spacing={2} sx={{ mb: 5, position: 'relative' }}>
    <Typography component="h1" variant="h4">Register to get started free</Typography>

      <Stack direction="row" spacing={0.5}>
        <Typography component="p" variant="body2"> Already have an account in SimpleT? </Typography>

        <Link href={paths.auth.firebase.login} component={RouterLink} variant="subtitle2">
          Sign in
        </Link>
      </Stack>
    </Stack>
  );

  const renderTerms = (
    <Typography
      component="div"
      sx={{ color: 'text.secondary', mt: 2.5, typography: 'caption', textAlign: 'center' }}
    >
      {'By signing up, I agree to '}
      <Link underline="always" color="text.primary" href={paths.legal.termsOfService}>
        Terms of Service
      </Link>
      {' and '}
      <Link underline="always" color="text.primary" href={paths.legal.privacyPolicy}>
        Privacy Policy
      </Link>
      .
    </Typography>
  );

  const renderForm = (
    <Stack spacing={2}>
      {!!errorMsg && <Alert severity="error" sx={{ maxHeight: 100}}>{errorMsg}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <RHFTextField name="firstName" label="First name" />
        <RHFTextField name="lastName" label="Last name" />
      </Stack>
      <RHFTextField name="email" label="Email address" disabled={!!inviteKey} />
      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {buttonText}
      </LoadingButton>
    </Stack>
  );
  const renderLoginOption = (
    <div>
      <Divider
        sx={{
          my: 2.5,
          typography: 'overline',
          color: 'text.disabled',
          '&::before, ::after': {
            borderTopStyle: 'dashed',
          },
        }}
      >
        OR
      </Divider>

      <Stack direction="row" justifyContent="center" spacing={2}>
        <IconButton onClick={handleGoogleLogin}>
          <Iconify icon="logos:google-icon"/>
        </IconButton>

        <IconButton onClick={showSalesforceLoginDialog.onTrue}>
          <Iconify icon="logos:salesforce"/>
        </IconButton>
      </Stack>
    </div>
  );

  return (
    <>
      {/* Required for reCAPTCHA */}
      <div id="recaptcha-container-for-register" />

      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderHead}
        {renderForm}
        {renderTerms}
        {!inviteKey && renderLoginOption}
      </FormProvider>
      <SalesforceLoginDialog open={showSalesforceLoginDialog.value} onClose={showSalesforceLoginDialog.onFalse} inviteKey={inviteKey} returnTo="" params=""/>
    </>
  );
}
